import { Avatar, Tooltip } from "@mui/material";
import React, { useState, useEffect } from "react";
import { getAvatarColor, getInitials } from "utils/avatarColors";

// The tint only ever shows through when the image is absent or fails to load,
// so it is applied unconditionally rather than branching on `src`.
const getAvatarProps = (name, style) => {
    const initials = getInitials(name);
    const { bg, fg } = getAvatarColor(name);
    return {
        sx: {
            bgcolor: bg,
            color: fg,
            width: "2.5rem",
            height: "2.5rem",
            fontSize: "1rem",
            fontWeight: 500,
            borderRadius: "9px",
            cursor: "pointer",
            position: "relative",
            ...style
        },
        children: initials,
    };
};

function ImageAvatar({ src, alt, width = "2.5rem", height = "2.5rem", style, tooltip = false, toolTipPlacement = "bottom", updatedDate = null }) {
    // Callers pass `row.original.image || ''` for startups with no logo. Concatenating
    // onto that produced `''` (and `"undefined"` when src was undefined), and MUI's
    // Avatar treats any string src as an image: it runs a load cycle, fails, THEN falls
    // back to the initials. That load->fail->fallback repeated on every mount, which is
    // the visible flicker on the letter avatars when the list re-renders (checkbox,
    // search). Resolving to `undefined` makes MUI skip the <img> entirely and paint the
    // initials directly - nothing to load, nothing to flicker.
    const resolvedSrc = src
        ? `${src}${updatedDate ? `?cacheBuster=${updatedDate}` : ""}`
        : undefined;

    return (
        <div style={{ position: "relative", display: "inline-block", width, height }}>
            {/* Avatar Background (Always Visible) */}
            {(tooltip ? (
                <Tooltip title={alt} placement={toolTipPlacement}>
                    <Avatar src={resolvedSrc} {...getAvatarProps(alt, style)} />
                </Tooltip>
            ) : (
                <Avatar src={resolvedSrc} {...getAvatarProps(alt, style)} />
            ))}
        </div>
    );
}

// Memoized: this is a pure presentational avatar, but it sits inside listing rows
// that re-render on unrelated state changes (e.g. row-selection checkbox clicks).
// Without memo, every such re-render re-rendered the avatar and reloaded its <img>,
// causing a visible flicker. Props (src/alt/width/height) are stable per row, so a
// shallow compare skips the re-render. Callers that pass a fresh inline `style`
// object simply don't benefit - they don't regress either.
export default React.memo(ImageAvatar);